import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { User } from '@models/user';
import { AuthenticationService } from '@services/authentication.service';
import { NgbModal, NgbNavChangeEvent } from '@ng-bootstrap/ng-bootstrap';
import { RequestService } from '@services/request.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DealerService } from '@app/services/dealer.service';
import { ToastrService } from 'ngx-toastr';
import { takeUntil } from 'rxjs/operators';
import { DataBuilderTableComponent } from '@app/widgets/data-builder-table/data-builder-table.component';
import { NgxSpinnerService } from 'ngx-spinner';

@Component({
	selector: 'app-user-list',
	templateUrl: './user-list.component.html',
	styleUrls: ['./user-list.component.scss'],
})
export class UserListComponent implements OnInit, OnDestroy {
	@ViewChild('fileUploadModal') fileUploadModal;
	@ViewChild('createUserModal') createUserModal;
	@ViewChild('adminTable') adminTableRef: DataBuilderTableComponent;
	@ViewChild('formazioneGlobaleTable') formazioneGlobaleTableRef: DataBuilderTableComponent;
	@ViewChild('formazioneRidottoTable') formazioneRidottoTableRef: DataBuilderTableComponent;

	private destroy$ = new Subject<void>();

	busy: Subscription;
	selectedTab: string = '';
	userTabLabel: string = '';
	urlClassificaItalia: string;
	urlClassificaAgenti: string;
	urlClassificaAreaManager: string;
	urlClassificaFormazione: string;
	urlClassificaTradeMarketing: string;
	urlClassificaAdmin: string;
	userForm: FormGroup;
	private user: User;
	stand = false;
	importLogs = [];
	importPartners = false;

	readonly tabLabels = {
		partner: 'Partner',
		admin: 'Admin',
		district: 'District',
		regional: 'Regional',
		formazione_globale: 'Formazione Globale',
		formazione_ridotto: 'Formazione Ridotto',
	};

	readonly tableRefs = {
		admin: () => this.adminTableRef,
		formazione_globale: () => this.formazioneGlobaleTableRef,
		formazione_ridotto: () => this.formazioneRidottoTableRef,
	};

	constructor(
		private modalService: NgbModal,
		private requestService: RequestService,
		private toastr: ToastrService,
		private authService: AuthenticationService,
		private fb: FormBuilder,
		private dealerService: DealerService,
		private spinnerService: NgxSpinnerService
	) {}

	ngOnInit() {
		this.authService.currentUser.pipe(takeUntil(this.destroy$)).subscribe((user) => {
			this.user = user;
			this.stand = user?.profile?.operatori?.includes('Kena Mobile Stands') || false;
		});

		this.selectedTab = 'admin';
		this.userTabLabel = 'Admin';

		this.userForm = this.fb.group({
			ragsociale: ['', [Validators.required, Validators.minLength(2)]],
			username: ['', [Validators.required, Validators.minLength(2)]],
			cellulare: ['', [Validators.required, Validators.pattern('^[0-9]{10}$')]],
			email: ['', [Validators.required, Validators.email]],
		});
	}

	onTabChange(event: NgbNavChangeEvent): void {
		this.selectedTab = event.nextId;
		this.userTabLabel = this.tabLabels[event.nextId];
	}

	updateUrls(operatore) {
		this.urlClassificaItalia = `dealers/table-list?vendor=${operatore}`;
		this.urlClassificaAgenti = `dealers/agent-table-list?vendor=${operatore}`;
		this.urlClassificaAreaManager = `dealers/master-agent-table-list?vendor=${operatore}`;
		this.urlClassificaFormazione = `dealers/formazione-table-list?vendor=${operatore}`;
		this.urlClassificaTradeMarketing = `dealers/trade-marketing-table-list?vendor=${operatore}`;
		this.urlClassificaAdmin = `dealers/admin-table-list?vendor=${operatore}`;
	}

	reloadComponent() {
		this.userForm.reset();
		this.userForm.markAsUntouched();

		const table = this.tableRefs[this.selectedTab]?.();

		if (table) {
			table.refresh();
		} else {
			console.warn(`No table to refresh for tab: ${this.selectedTab}`);
		}
	}

	showFileUpload() {
		this.importLogs = [];
		this.modalService.open(this.fileUploadModal, { size: 'xl' });
	}

	showCreateUserModal() {
		this.modalService.open(this.createUserModal, { size: 'md' });
	}

	closeUserModal() {
		this.modalService.dismissAll(this.createUserModal);
	}

	createUser() {
		this.spinnerService.show('creatingUser');
		const payload = {
			...this.userForm.value,
			userType: this.selectedTab,
		};

		this.dealerService
			.createUser(payload)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (response: any) => {
					const title = response.success ? 'Operazione completata!' : 'Errore';
					const type = response.success ? 'success' : 'error';
					this.spinnerService.hide('creatingUser');
					this.closeUserModal();

					if (!response.success && response.errors) {
						const detailedErrors = this.formatErrors(response.errors);
						this.showToast(type, `${response.message}<br><br>${detailedErrors}`, title, true);
					} else {
						this.showToast(type, response.message, title);
					}

					this.reloadComponent();
				},

				error: (err) => {
					this.spinnerService.hide('creatingUser');
					this.closeUserModal();
					this.showToast('error', 'Errore imprevisto durante la creazione utente.', 'Errore');
					console.error('Errore createUser():', err);
				},
			});
	}

	uploadFile($event: Event) {
		// @ts-ignore
		const file = $event.target.files.item(0);
		if (!file) {
			return;
		}

		const formData = new FormData();
		formData.append('file', file, file.name);

		this.importLogs = [];
		this.importPartners = true;
		this.busy = this.requestService
			.post(`eolo/partner/import`, formData)
			.pipe(takeUntil(this.destroy$))
			.subscribe(
				(data: any) => {
					this.showToast('success', 'File caricato con successo', 'Operazione completata!');
					this.importLogs = data;
					this.importPartners = false;
				},
				(err) => {
					this.showToast('error', 'Errore nel caricamento del file', 'Errore!');
					this.importPartners = false;
				}
			);
	}

	private showToast(
		type: 'success' | 'error',
		message: string,
		title: string,
		enableHtml: boolean = false
	) {
		this.toastr[type](message, title, {
			progressBar: true,
			progressAnimation: 'increasing',
			timeOut: 7000,
			closeButton: true,
			positionClass: 'toast-top-right',
			tapToDismiss: true,
			enableHtml: enableHtml,
		});
	}

	private formatErrors(errors: Record<string, string[]>): string {
		return Object.entries(errors)
			.map(([field, messages]) => `<b>${field}:</b> ${messages.join(', ')}`)
			.join('<br>');
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
